/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import numeral from 'numeral';
import { get } from 'lodash/fp';
import moment from 'moment';

import Icon from 'app/components/commons/Icon';
import Button from 'app/components/commons/Button';
import bonusBalanceSelector from 'app/selectors/bonusBalanceSelector';
import { DAILY_BONUS, DAILY_SPINS, REFER_A_FRIEND, TICKET } from '../../constants/bonusTypes';
import { acquireCredit } from 'app/actions/userActions';
import { rewritePath } from 'app/utils/routeUtils';

type Props = {
  userTotalBonus?: number,
  bonusBalance: number,
  userBonuses: any,
  rewriteRules: Object,
  history: Object,
  claimDailyBonus: () => void
};

const AccountBonus = ({
  bonusBalance,
  userBonuses,
  rewriteRules,
  history,
  claimDailyBonus
}: Props) => {
  const ticketBonuses = userBonuses.filter(bonus => bonus.type === TICKET);
  const otherBonuses = userBonuses.filter(bonus => bonus.type !== TICKET);

  const ticketBonusClaimed = (bonus: any) => {
    if (bonus.type === DAILY_BONUS && bonus.unclaimed) {
      claimDailyBonus();
    } else if (bonus.type === DAILY_SPINS && bonus.value === undefined) {
      history.push('/spin-and-win');
    } else if (bonus.type === REFER_A_FRIEND) {
      history.push({ search: '?modal=referral' });
    } else if (bonus.type === TICKET && bonus.challenge) {
      const challengeLink = rewritePath(rewriteRules)(`/challenge/${get('id', bonus.challenge)}`);

      if (get('location.pathname', window) !== challengeLink) {
        history.push(challengeLink);
      }
    }
  };

  return (
    <div>
      <Helmet title="Bonus Wallet" />
      <AccountBonusContainer>
        <BonusRow>
          <HorizontalContainer>
            <StyledIcon type="icon-menu-bonus" />
            <VerticalContainer>
              <DarkText>Bonus</DarkText>
            </VerticalContainer>
          </HorizontalContainer>
          <VerticalContainer>
            <DarkText>{`$${numeral(bonusBalance).format('0,0.00')}`}</DarkText>
          </VerticalContainer>
        </BonusRow>
      </AccountBonusContainer>
      <AccountBonusContainer>
        <HeaderSection>
          <Header>Bonus Wallet</Header>
        </HeaderSection>
        <ContentSection>
          {otherBonuses.map((bonus, i) => {
            const currentTime = moment();
            const expiryDate = moment(bonus.expiry);
            const duration = moment.duration(expiryDate.diff(currentTime));

            return (
              <BonusRow key={get('id', bonus.challenge) || i}>
                <HorizontalContainer>
                  <StyledIcon type="icon-menu-bonus" />
                  <VerticalContainer>
                    <DarkText>Bonus</DarkText>
                    <SmallGreyText>
                      Date: {moment(bonus.created).format('DD/MM/YYYY')}
                    </SmallGreyText>
                  </VerticalContainer>
                </HorizontalContainer>
                <VerticalContainer>
                  <DarkText>{`$${numeral(bonus.value).format('0,0.00')}`}</DarkText>
                  <SmallDangerText>Expires {duration.humanize(true)}</SmallDangerText>
                </VerticalContainer>
              </BonusRow>
            );
          })}
          {ticketBonuses.map((bonus, i) => {
            return (
              <BonusRow key={get('id', bonus.challenge) || i}>
                <HorizontalContainer>
                  <StyledImage src={bonus.icon} />
                  <VerticalContainer>
                    <DarkText>
                      {bonus.type === TICKET && bonus.challenge
                        ? get('name', bonus.challenge)
                        : bonus.label}
                    </DarkText>
                  </VerticalContainer>
                </HorizontalContainer>
                <VerticalContainer>
                  <ClaimButton isFetching={false} onClick={() => ticketBonusClaimed(bonus)}>
                    {bonus.buttonName || 'Claim'}
                  </ClaimButton>
                </VerticalContainer>
              </BonusRow>
            );
          })}
        </ContentSection>
      </AccountBonusContainer>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    bonusBalance: bonusBalanceSelector(state),
    userBonuses: get('currentUser.bonusPack.bonuses', state.user) || [],
    rewriteRules: state.settings.rewriteRules
  };
}

const actions = {
  claimDailyBonus: acquireCredit
};

export default withRouter(connect(mapStateToProps, actions)(AccountBonus));

const AccountBonusContainer = styled.div`
  border-radius: 10px;
  background-color: white;
  margin-bottom: 20px;
`;

const HeaderSection = styled.div`
  width: 100%;
  padding: 10px 30px;
  background-color: #dce0e9;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
`;

const Header = styled.h2(({ theme }) => ({
  color: theme.color.basis1
}));

const ContentSection = styled.div`
  padding: 10px;
`;

const BonusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px 20px;

  &:not(:last-child) {
    border-bottom: 1px solid black;
  }
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledIcon = styled(Icon)(({ theme }) => ({
  width: 25,
  height: 25,
  marginRight: 15,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.color.primary
}));

const StyledImage = styled.img({
  height: 25,
  marginRight: 15,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const DarkText = styled.p(({ theme }) => ({
  fontWeight: theme.fontWeight.demiBold,
  color: theme.color.basis1,
  fontSize: 24,
  marginBottom: 5
}));

const SmallGreyText = styled.p(({ theme }) => ({
  color: theme.color.basis4,
  fontSize: 16,
  marginBottom: 0
}));

const SmallDangerText = styled.p(({ theme }) => ({
  color: theme.color.danger,
  fontSize: 16,
  marginBottom: 0
}));

const ClaimButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.color.blue,
  borderRadius: 3,
  color: theme.color.white,
  padding: '0.5em 1.5em',
  textTransform: 'uppercase',
  minWidth: 70
}));
